import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, Search, X } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import type { FormularyItem } from '../types/clinical';

export function FormularyPage() {
  const { get, post, patch } = useApi();
  const { userRole } = useAuth();
  const isDoctor = userRole === 'doctor';

  const [items, setItems] = useState<FormularyItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [genericName, setGenericName] = useState('');
  const [defaultDose, setDefaultDose] = useState('');
  const [defaultFrequency, setDefaultFrequency] = useState('');
  const [defaultDuration, setDefaultDuration] = useState('');
  const [nappiCode, setNappiCode] = useState('');
  const [category, setCategory] = useState('');

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const result = await get<FormularyItem[]>(`/formulary?include_inactive=${showInactive}`);
      if (result.success && result.data) setItems(result.data);
      else setError(result.error || 'Failed to load formulary');
    } catch {
      setError('Failed to load formulary');
    } finally {
      setLoading(false);
    }
  }, [get, showInactive]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const resetForm = () => {
    setName(''); setGenericName(''); setDefaultDose(''); setDefaultFrequency('');
    setDefaultDuration(''); setNappiCode(''); setCategory('');
    setEditingId(null); setShowForm(false);
  };

  const handleSave = useCallback(async () => {
    if (!name) return;
    const body = { name, genericName, defaultDose, defaultFrequency, defaultDuration, nappiCode, category };
    let result;
    if (editingId) {
      result = await patch(`/formulary/${editingId}`, body);
    } else {
      result = await post('/formulary', body);
    }
    if (result.success) {
      resetForm();
      fetchItems();
    } else {
      setError(result.error || `Failed to ${editingId ? 'update' : 'add'} drug`);
    }
  }, [name, genericName, defaultDose, defaultFrequency, defaultDuration, nappiCode, category, editingId, post, patch, fetchItems]);

  const handleEdit = (item: FormularyItem) => {
    setEditingId(item.id);
    setName(item.name);
    setGenericName(item.genericName);
    setDefaultDose(item.defaultDose);
    setDefaultFrequency(item.defaultFrequency);
    setDefaultDuration(item.defaultDuration);
    setNappiCode(item.nappiCode);
    setCategory(item.category);
    setShowForm(true);
  };

  const handleToggleActive = async (item: FormularyItem) => {
    const result = await patch(`/formulary/${item.id}`, { active: !item.active });
    if (result.success) {
      fetchItems();
    } else {
      setError(result.error || `Failed to ${item.active ? 'deactivate' : 'reactivate'} drug`);
    }
  };

  const filtered = items.filter(i => {
    const q = search.toLowerCase();
    return !q || i.name.toLowerCase().includes(q) || i.genericName.toLowerCase().includes(q);
  });

  return (
    <main className="max-w-5xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-gray-900">Drug Formulary</h1>
        {isDoctor && (
          <button onClick={() => { resetForm(); setShowForm(true); }} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 flex items-center gap-2">
            <Plus className="h-4 w-4" /> Add Drug
          </button>
        )}
      </div>

      {error && <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

      <div className="flex items-center gap-4 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search drugs..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md" />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} className="h-4 w-4" /> Show inactive
        </label>
      </div>

      {showForm && (
        <div className="mb-6 bg-white rounded-lg shadow-sm border p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold">{editingId ? 'Edit Drug' : 'Add Drug'}</h3>
            <button onClick={resetForm}><X className="h-4 w-4 text-gray-400" /></button>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Drug name" className="px-3 py-2 text-sm border rounded" />
            <input value={genericName} onChange={e => setGenericName(e.target.value)} placeholder="Generic name" className="px-3 py-2 text-sm border rounded" />
            <input value={defaultDose} onChange={e => setDefaultDose(e.target.value)} placeholder="Default dose" className="px-3 py-2 text-sm border rounded" />
            <input value={defaultFrequency} onChange={e => setDefaultFrequency(e.target.value)} placeholder="Default frequency" className="px-3 py-2 text-sm border rounded" />
            <input value={defaultDuration} onChange={e => setDefaultDuration(e.target.value)} placeholder="Default duration" className="px-3 py-2 text-sm border rounded" />
            <input value={nappiCode} onChange={e => setNappiCode(e.target.value)} placeholder="NAPPI code" className="px-3 py-2 text-sm border rounded" />
            <input value={category} onChange={e => setCategory(e.target.value)} placeholder="Category" className="px-3 py-2 text-sm border rounded" />
          </div>
          <button onClick={handleSave} disabled={!name} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50">
            {editingId ? 'Update' : 'Add'}
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>
      ) : filtered.length === 0 ? (
        <p className="text-center py-8 text-gray-500">No drugs in formulary.</p>
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Generic</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Default Dose</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Frequency</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filtered.map(item => (
                <tr key={item.id} className={!item.active ? 'opacity-50' : ''}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{item.genericName || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{item.defaultDose || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{item.defaultFrequency || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{item.category || '—'}</td>
                  <td className="px-4 py-3 text-sm text-right">
                    {isDoctor && (
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => handleEdit(item)} className="text-blue-600 hover:text-blue-800 text-xs">Edit</button>
                        <button onClick={() => handleToggleActive(item)} className={`text-xs ${item.active ? 'text-red-600 hover:text-red-800' : 'text-green-600 hover:text-green-800'}`}>
                          {item.active ? 'Deactivate' : 'Reactivate'}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}
